import React from 'react'
import { Modal, Button } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import { modalStyles, titleStyle, buttonStyles, modalContainerStyle, tableBorder, tableCellStyle, tableHeaderStyle } from './modal-styles'
import palletSticker from '../../../static/pdf/pallet-stickers/pallet-sticker'

interface MergedPalletItemDto {
  poDetailId: number
  itemCode: string
  description: string
  qty: number
  boxIds: string[]
}

interface MergedPalletDto {
  plateId: number
  plateCode: string
  items: MergedPalletItemDto[]
}

interface MergeItemsToPalletResultDto {
  pallets: MergedPalletDto[]
}

interface MergeSuccessModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  mergedItems: any[]
  palletResult: MergeItemsToPalletResultDto | null
  onSuccess: () => void
}

const MergeSuccessModal: React.FC<MergeSuccessModalProps> = ({
  visible,
  onClose,
  poData,
  mergedItems,
  palletResult,
  onSuccess
}) => {
  // 关闭模态框
  const handleClose = () => {
    onSuccess() // 刷新列表
    onClose()
  }

  // 打印托盘 barcode
  const handlePrintPalletBarcode = () => {
    if (palletResult?.pallets && palletResult.pallets.length > 0) {
      const palletCodes = palletResult.pallets.map(p => p.plateCode).filter(Boolean)
      if (palletCodes.length > 0) {
        palletSticker(palletCodes, 'print')
      }
    }
  }

  // 计算总数量
  const getTotalQty = () => {
    return mergedItems.reduce((sum, item) => sum + (item.totalRevQty || 0), 0)
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleClose}
      width={1000}
      footer={null}
      closable={true}
      style={{ top: 50 }}
      bodyStyle={{ padding: modalStyles.padding, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          Merge Success
        </div>

        {/* 成功消息 */}
        <div style={{
          backgroundColor: '#f0fdf4',
          border: `1px solid ${modalStyles.success}`,
          borderRadius: modalStyles.borderRadiusSmall,
          padding: '16px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 600,
            color: modalStyles.success,
            marginBottom: '8px'
          }}>
            ✓ Merge Successful
          </div>
          <div style={{
            fontSize: '14px',
            color: modalStyles.textPrimary
          }}>
            All product has been put away to <strong>{palletResult?.pallets?.[0]?.plateCode || 'Pallet'}</strong>
          </div>
        </div>

        {/* 托盘信息 */}
        {palletResult && palletResult.pallets && palletResult.pallets.length > 0 && (
          <div style={{
            backgroundColor: modalStyles.bgWhite,
            border: `1px solid ${modalStyles.borderSecondary}`,
            borderRadius: modalStyles.borderRadiusSmall,
            padding: modalStyles.paddingSmall,
            marginBottom: '20px'
          }}>
            <div style={{
              borderBottom: `2px solid ${modalStyles.borderSecondary}`,
              marginBottom: '16px',
              paddingBottom: '12px'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: modalStyles.info,
                borderBottom: `2px solid ${modalStyles.info}`,
                paddingBottom: '10px',
                display: 'inline-block'
              }}>
                Pallet Information
              </span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${modalStyles.borderSecondary}` }}>
                  <td style={{ padding: '12px 8px', width: '25%', fontSize: '14px', color: modalStyles.textSecondary, fontWeight: 500 }}>
                    Pallet Code
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '14px', color: modalStyles.textPrimary, fontWeight: 600 }}>
                    {palletResult.pallets[0].plateCode}
                  </td>
                  <td style={{ padding: '12px 8px', width: '25%', fontSize: '14px', color: modalStyles.textSecondary, fontWeight: 500 }}>
                    Total Quantity
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '14px', color: modalStyles.success, fontWeight: 600 }}>
                    {getTotalQty()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 合并的物料列表 */}
        {mergedItems.length > 0 && (
          <div style={{
            backgroundColor: modalStyles.bgWhite,
            border: `1px solid ${modalStyles.borderSecondary}`,
            borderRadius: modalStyles.borderRadiusSmall,
            padding: modalStyles.paddingSmall,
            marginBottom: '20px'
          }}>
            <div style={{
              borderBottom: `2px solid ${modalStyles.borderSecondary}`,
              marginBottom: '16px',
              paddingBottom: '12px'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: modalStyles.primary,
                borderBottom: `2px solid ${modalStyles.primary}`,
                paddingBottom: '10px',
                display: 'inline-block'
              }}>
                Merged Items ({mergedItems.length})
              </span>
            </div>

            <table style={{ width: '100%', ...tableBorder, borderRadius: modalStyles.borderRadiusSmall, overflow: 'hidden', boxShadow: modalStyles.shadowSmall }}>
              <thead>
                <tr style={tableHeaderStyle}>
                  <th style={{ ...tableCellStyle, textAlign: 'left' }}>Item Code</th>
                  <th style={{ ...tableCellStyle, textAlign: 'left' }}>Description</th>
                  <th style={{ ...tableCellStyle, textAlign: 'center' }}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {mergedItems.map((item, index) => (
                  <tr key={item.poDetailId} style={{ backgroundColor: index % 2 === 0 ? modalStyles.bgWhite : modalStyles.bgGray }}>
                    <td style={{ ...tableCellStyle, color: modalStyles.textPrimary, fontWeight: 500 }}>
                      {item.rawMaterial?.rawMaterialCode || 'N/A'}
                    </td>
                    <td style={{ ...tableCellStyle, color: modalStyles.textPrimary }}>
                      <div style={{ wordBreak: 'break-word' }}>
                        {item.rawMaterial?.rawMaterialName || 'N/A'}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        backgroundColor: modalStyles.bgGray,
                        border: `1px solid ${modalStyles.borderSecondary}`,
                        borderRadius: modalStyles.borderRadiusTiny,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: modalStyles.textPrimary,
                        minWidth: '60px'
                      }}>
                        {item.totalRevQty || 0}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部操作按钮区 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: `2px solid ${modalStyles.borderSecondary}` }}>
          <Button
            onClick={handlePrintPalletBarcode}
            icon={<PrinterOutlined />}
            style={{
              ...buttonStyles.success,
              minWidth: '140px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#065f46'
              e.currentTarget.style.borderColor = '#065f46'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = modalStyles.success
              e.currentTarget.style.borderColor = modalStyles.success
            }}
          >
            Print Pallet Barcode
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              onClick={handleClose}
              style={{
                ...buttonStyles.secondary,
                minWidth: '120px',
                lineHeight: '1.5',
                padding: '8px 20px',
                fontSize: '14px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = modalStyles.bgLight
                e.currentTarget.style.borderColor = modalStyles.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff'
                e.currentTarget.style.borderColor = modalStyles.primary
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MergeSuccessModal
